import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PollCard from "../components/PollCard";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const HomePage = () => {
  const { user, loading: authLoading } = useAuth();
  const requestIdRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { pollId } = useParams();
  const routePollId = pollId || (window.location.pathname.startsWith("/poll/")
    ? window.location.pathname.replace("/poll/", "")
    : "");
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [reportedPollIds, setReportedPollIds] = useState(() => new Set());
  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  }, [location.search]);

  useEffect(() => {
    const fetchPolls = async () => {
      if (authLoading) return;
      requestIdRef.current += 1;
      const activeRequest = requestIdRef.current;
      setPolls([]);
      setLoading(true);
      try {
        if (routePollId) {
          const response = await api.get(`/polls/${routePollId}`);
          if (requestIdRef.current !== activeRequest) return;
          setPolls([response.data]);
        } else {
          const response = await api.get("/polls");
          if (requestIdRef.current !== activeRequest) return;
          setPolls(response.data);
        }
      } catch (error) {
        if (requestIdRef.current !== activeRequest) return;
        toast.error("Unable to load the feed right now.");
        setPolls([]);
      } finally {
        if (requestIdRef.current !== activeRequest) return;
        setLoading(false);
      }
    };
    fetchPolls();
  }, [authLoading, routePollId, user, refreshTick]);

  useEffect(() => {
    const handleFollowChange = () => {
      setRefreshTick((prev) => prev + 1);
    };
    window.addEventListener("follow-state-changed", handleFollowChange);
    return () => window.removeEventListener("follow-state-changed", handleFollowChange);
  }, []);

  const updatePoll = (updatedPoll) => {
    setPolls((prev) => prev.map((poll) => (poll.id === updatedPoll.id ? updatedPoll : poll)));
  };

  const removePoll = (pollIdToRemove) => {
    setPolls((prev) => prev.filter((poll) => poll.id !== pollIdToRemove));
  };

  const filteredPolls = useMemo(() => {
    if (!searchQuery.trim()) return polls;
    const query = searchQuery.toLowerCase().replace("@", "");
    return polls.filter((poll) => {
      const optionText = poll.options?.map((option) => option.text?.toLowerCase()).join(" ") || "";
      return (
        poll.question?.toLowerCase().includes(query) ||
        poll.creator?.username?.toLowerCase().includes(query) ||
        optionText.includes(query)
      );
    });
  }, [polls, searchQuery]);

  const visiblePolls = useMemo(() => {
    if (!reportedPollIds.size) return filteredPolls;
    return filteredPolls.filter((poll) => !reportedPollIds.has(poll.id));
  }, [filteredPolls, reportedPollIds]);

  const handleReport = (pollIdToHide) => {
    setReportedPollIds((prev) => {
      const next = new Set(prev);
      next.add(pollIdToHide);
      return next;
    });
    setPolls((prev) => prev.filter((poll) => poll.id !== pollIdToHide));
  };

  const matchedUsers = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().replace("@", "");
    const unique = new Map();
    polls.forEach((poll) => {
      const username = poll.creator?.username || "";
      if (username.toLowerCase().includes(query)) {
        unique.set(username, poll.creator);
      }
    });
    return Array.from(unique.values());
  }, [polls, searchQuery]);

  return (
    <div className="space-y-6" data-testid="home-page">
      <section className="space-y-4" data-testid="home-hero">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold" data-testid="home-title">
              {routePollId ? "Shared poll" : "Global feed"}
            </h2>
            <p className="text-sm text-muted-foreground" data-testid="home-subtitle">
              {routePollId
                ? "This poll lives directly in the feed."
                : "Real opinions, real time."}
            </p>
          </div>
          <Button
            onClick={() => (user ? navigate("/create") : navigate("/auth"))}
            data-testid="home-create-poll-button"
          >
            Create poll
          </Button>
        </div>
        {searchQuery.trim() && matchedUsers.length > 0 && (
          <div
            className="rounded-xl border border-border bg-card p-3 text-sm"
            data-testid="home-search-users"
          >
            <div className="text-xs uppercase text-muted-foreground" data-testid="home-search-users-label">
              People
            </div>
            <div className="mt-2 space-y-2">
              {matchedUsers.map((creator) => (
                <button
                  key={creator.username}
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => navigate(`/user/${creator.username}`)}
                  data-testid={`home-search-user-${creator.username}`}
                >
                  <img
                    src={creator.avatar_url}
                    alt={creator.username}
                    className="h-6 w-6 rounded-full object-cover"
                  />
                  <span>@{creator.username}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {!user && (
          <div
            className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground"
            data-testid="home-signup-hint"
          >
            Sign in to vote, comment, and publish your own polls.
          </div>
        )}
      </section>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="home-loading">
          Loading polls...
        </div>
      ) : (
        <div className="space-y-7" data-testid="home-feed">
          {visiblePolls.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="home-empty">
              No polls found.
            </div>
          ) : (
            visiblePolls.map((poll) => (
              <PollCard key={poll.id} poll={poll} onUpdate={updatePoll} onRemove={removePoll} onReport={handleReport} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default HomePage;
